/** Serializable contracts shared by browser projections and authoritative server operations. */
export type HipaaDocumentWorkflow = 'complete-upload' | 'assemble';
export type HipaaDocumentRole = 'complete-authorization' | 'signed-pages-source' | 'facility-packet';
export const HIPAA_MAX_PDF_BYTES = 10 * 1024 * 1024;
export const HIPAA_MAX_PAGES = 100;
export const ASSEMBLY_RENDERER_VERSION = 'assembly-v1';

export interface ImmutablePdfReference {
  storagePath: string;
  objectGeneration: string;
  sha256: string;
}
export interface AssemblyVersion {
  workflow: 'assemble';
  schemaVersion: 1;
  name: string;
  objectGeneration: string;
  reusablePageOrder: number[];
  signedPageInstructions: string;
  signedPageCount: number;
  rendererVersion: string;
  approvedBy: string;
  lifecycle: 'active' | 'retired' | 'revoked';
  lifecycleRevision: number;
}
export interface HipaaSourceCandidate extends ImmutablePdfReference {
  documentId: string;
  revision: number;
  pageCount: number;
  signatureReceiptId?: string;
  originalDocumentId?: string;
}
export interface HipaaAssemblyAssociation extends HipaaSourceCandidate {
  firmId: string;
  clientId: string;
  versionId: string;
  signatureReceiptId: string;
  confirmedBy: string;
  confirmedAt: string;
}
export type AssemblyReadiness = 'setup-required' | 'activation-required' | 'source-missing' |
  'signature-pending' | 'association-required' | 'facility-incomplete' | 'ready-to-assemble' |
  'ready-packet' | 'blocked-revoked';

/** Advisory display projection. A pending candidate never replaces these current-source inputs. */
export function projectAssemblyReadiness(input: {
  activated: boolean; hasVersion: boolean; hasSource: boolean; signatureVerified: boolean;
  associationConfirmed: boolean; facilityComplete: boolean; packetCurrent?: boolean;
  version?: Pick<AssemblyVersion, 'lifecycle'>;
}): AssemblyReadiness {
  if (!input.hasVersion) return 'setup-required';
  if (!input.activated) return 'activation-required';
  if (input.version?.lifecycle === 'revoked') return 'blocked-revoked';
  if (!input.hasSource) return 'source-missing';
  if (!input.signatureVerified) return 'signature-pending';
  if (!input.associationConfirmed) return 'association-required';
  if (!input.facilityComplete) return 'facility-incomplete';
  return input.packetCurrent ? 'ready-packet' : 'ready-to-assemble';
}

export function validateAssemblyVersion(version: AssemblyVersion, pageCount: number): void {
  if (version.workflow !== 'assemble' || version.schemaVersion !== 1 ||
      !version.name?.trim() || !/^\d+$/.test(version.objectGeneration) || !version.approvedBy ||
      !version.signedPageInstructions?.trim() || !Number.isSafeInteger(version.signedPageCount) ||
      version.signedPageCount < 1 || !Number.isSafeInteger(pageCount) || pageCount < 1 ||
      pageCount + version.signedPageCount > HIPAA_MAX_PAGES ||
      version.rendererVersion !== ASSEMBLY_RENDERER_VERSION ||
      !['active', 'retired', 'revoked'].includes(version.lifecycle) ||
      !Number.isSafeInteger(version.lifecycleRevision) || version.lifecycleRevision < 1 ||
      !Array.isArray(version.reusablePageOrder) || version.reusablePageOrder.length !== pageCount ||
      version.reusablePageOrder.some((page, index) => page !== index + 1)) {
    throw new Error('Review the reusable pages, page order and expected signed pages before publishing.');
  }
}

/** Key-order-independent serialization for comparing Firestore values across reads. */
export function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`).join(',')}}`;
  return JSON.stringify(value);
}

export const ORIGINAL_HIPAA_DOCUMENT_TYPES: readonly string[] = ['hipaa_auth', 'hipaa_auth_general', 'hipaa_auth_signed'];
export interface HipaaUploadFields {
  documentType?: unknown; providerSpecific?: unknown; hipaaDocumentRole?: unknown;
  hipaaSourceCandidate?: unknown; protectedDocumentStatus?: unknown;
}
/** A client's own signed upload: never a provider copy, generated packet, pending candidate or replaced file. */
export function isOriginalHipaaUpload(doc: HipaaUploadFields): boolean {
  return typeof doc.documentType === 'string' && ORIGINAL_HIPAA_DOCUMENT_TYPES.includes(doc.documentType) &&
    !doc.providerSpecific && doc.hipaaDocumentRole !== 'facility-packet' && doc.hipaaSourceCandidate !== true &&
    (!doc.protectedDocumentStatus || doc.protectedDocumentStatus === 'active');
}

export interface RegisteredHipaaFields {
  hipaaAuthorizationStoragePath?: unknown; hipaaAuthorizationFile?: unknown; hipaaAuthorizationUrl?: unknown;
}
/**
 * Storage path of the client's registered signed authorization, or null when none is on file.
 * Old clients sometimes have only an own-bucket Firebase download URL; `bucketName` is read
 * only for that fallback.
 */
export function registeredHipaaBasePath(client: RegisteredHipaaFields, bucketName: () => string): string | null {
  if (typeof client.hipaaAuthorizationStoragePath === 'string' && client.hipaaAuthorizationStoragePath) return client.hipaaAuthorizationStoragePath;
  if (typeof client.hipaaAuthorizationFile === 'string' && client.hipaaAuthorizationFile && !client.hipaaAuthorizationFile.startsWith('https:')) return client.hipaaAuthorizationFile;
  try {
    const url = new URL(String(client.hipaaAuthorizationUrl));
    const bucket = bucketName();
    if (url.protocol !== 'https:' || !bucket) return null;
    const prefix = `/v0/b/${bucket}/o/`;
    if (url.hostname === 'firebasestorage.googleapis.com' && url.pathname.startsWith(prefix)) return decodeURIComponent(url.pathname.slice(prefix.length));
    if (url.hostname === 'storage.googleapis.com' && url.pathname.startsWith(`/${bucket}/`)) return decodeURIComponent(url.pathname.slice(bucket.length + 2));
  } catch { /* invalid legacy URL: nothing registered */ }
  return null;
}
